import {refreshDatatableScrollHintForTable} from './datatable-scroll-hint';

type ServerSideDatatable = HTMLElement & {
  enablePagination?: boolean;
  data?: unknown[];
  columns?: Array<Record<string, unknown>>;
  requestUpdate?: () => void;
  _pagination?: { pageIndex: number; pageSize: number };
  _handlePaginationChange?: (
    next: { pageIndex: number; pageSize: number },
  ) => void;
};

type CbarSelectElement = HTMLElement & {
  items?: Array<{ id: string; title: string }>;
  value?: string;
};

export type ServerPaginationController = {
  setState: (pageIndex: number, pageSize: number, totalRecords: number) => void;
  getPageSize: () => number;
};

function setDatatablePageSize(table: ServerSideDatatable, pageSize: number): void {
  const normalizedPageSize = Math.max(1, pageSize);
  if (typeof table._handlePaginationChange === 'function') {
    table._handlePaginationChange({ pageIndex: 0, pageSize: normalizedPageSize });
    return;
  }

  table._pagination = { pageIndex: 0, pageSize: normalizedPageSize };
  table.requestUpdate?.();
}

/**
 * cbar-datatable always applies TanStack pagination internally (default page size 10)
 * even when enablePagination hides the built-in pager. Match the server fetch size
 * so every row returned by the API is visible.
 */
export function configureServerSideDatatable(
  table: ServerSideDatatable,
  pageSize: number,
): void {
  table.enablePagination = false;
  window.CBAR?.applyServerSideDatatableShell?.(table as HTMLElement & {dataset: DOMStringMap});
  setDatatablePageSize(table, pageSize);
}

/** Update server-driven datatable rows and keep client pagination in sync. */
export function setServerSideDatatableData(
  table: ServerSideDatatable,
  data: unknown[],
  pageSize: number,
): void {
  configureServerSideDatatable(table, pageSize);
  table.data = Array.isArray(data) ? data : [];
  table.requestUpdate?.();
  requestAnimationFrame(() => {
    refreshDatatableScrollHintForTable(table);
  });
}

function parsePageSizeFromSelectEvent(
  event: Event,
  select: CbarSelectElement,
  fallback: number,
): number {
  const detail = (event as CustomEvent<{ value?: string }>).detail;
  const raw = detail?.value ?? select.value ?? null;
  const parsed = parseInt(String(raw ?? fallback), 10);
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : fallback;
}

export async function createServerPagination(
  paginationRoot: HTMLElement,
  onPageChange: (pageIndex: number, pageSize: number) => void,
  initialPageSize = 25,
): Promise<ServerPaginationController> {
  if (!customElements.get('cbar-select')) {
    await customElements.whenDefined('cbar-select');
  }

  let pageIndex = 0;
  let pageSize = initialPageSize;
  let totalRecords = 0;

  const info = document.createElement('div');
  info.className = 'cbar-server-pagination__info';

  const pageSizeLabel = document.createElement('label');
  pageSizeLabel.textContent = 'Rows per page:';

  const pageSizeSelect = document.createElement('cbar-select') as CbarSelectElement;
  pageSizeSelect.setAttribute('variant', 'outlined');
  pageSizeSelect.items = [
    {id: '10', title: '10'},
    {id: '25', title: '25'},
    {id: '50', title: '50'},
  ];
  pageSizeSelect.value = String(pageSize);

  const pageSizeWrap = document.createElement('div');
  pageSizeWrap.className = 'cbar-server-pagination__page-size';
  pageSizeWrap.append(pageSizeLabel, pageSizeSelect);

  const pageInfo = document.createElement('span');
  pageInfo.className = 'cbar-server-pagination__page-info';

  const controls = document.createElement('div');
  controls.className = 'cbar-server-pagination__controls';

  const makeButton = (label: string, handler: () => void) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', handler);
    return button;
  };

  const render = () => {
    const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize));
    const from = totalRecords === 0 ? 0 : pageIndex * pageSize + 1;
    const to = Math.min((pageIndex + 1) * pageSize, totalRecords);

    info.textContent = `Showing ${from} to ${to} of ${totalRecords.toLocaleString('en-AU')} entries`;
    pageInfo.textContent = `Page ${pageIndex + 1} of ${pageCount.toLocaleString('en-AU')}`;

    const buttons = controls.querySelectorAll('button');
    buttons.forEach((button, index) => {
      if (index === 0 || index === 1) {
        button.disabled = pageIndex <= 0;
      } else if (index === 2 || index === 3) {
        button.disabled = pageIndex >= pageCount - 1;
      }
    });

    paginationRoot.hidden = false;
  };

  const handlePageSizeChange = (event: Event) => {
    const nextSize = parsePageSizeFromSelectEvent(event, pageSizeSelect, pageSize);
    if (nextSize === pageSize) {
      return;
    }
    pageSize = nextSize;
    pageIndex = 0;
    onPageChange(pageIndex, pageSize);
  };

  pageSizeSelect.addEventListener('change', handlePageSizeChange);

  controls.append(
    makeButton('First', () => {
      if (pageIndex > 0) {
        pageIndex = 0;
        onPageChange(pageIndex, pageSize);
      }
    }),
    makeButton('Previous', () => {
      if (pageIndex > 0) {
        pageIndex -= 1;
        onPageChange(pageIndex, pageSize);
      }
    }),
    pageInfo,
    makeButton('Next', () => {
      const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize));
      if (pageIndex < pageCount - 1) {
        pageIndex += 1;
        onPageChange(pageIndex, pageSize);
      }
    }),
    makeButton('Last', () => {
      const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize));
      if (pageIndex < pageCount - 1) {
        pageIndex = pageCount - 1;
        onPageChange(pageIndex, pageSize);
      }
    }),
  );

  const wrap = document.createElement('div');
  wrap.className = 'cbar-server-pagination__inner';
  wrap.append(info, pageSizeWrap, controls);
  paginationRoot.replaceChildren(wrap);

  return {
    setState(nextPageIndex: number, nextPageSize: number, nextTotalRecords: number) {
      pageIndex = nextPageIndex;
      pageSize = nextPageSize;
      totalRecords = nextTotalRecords;
      if (pageSizeSelect.value !== String(pageSize)) {
        pageSizeSelect.value = String(pageSize);
      }
      render();
    },
    getPageSize: () => pageSize,
  };
}
